import { getAuthHeaders } from '../utils/auth';

export interface InvitedUser {
  id?: string;
  email: string;
  invitationType: 'album' | 'event' | 'trip' | 'network';
  invitationId: string;
  inviterUserId: string;
  createdAt?: any;
  expiresAt?: any;
  used: boolean;
  invitations?: Record<string, any>;
}

/**
 * Check if a user is invited (for subscription bypass during signup/login)
 */
export const checkInvitedUser = async (email: string): Promise<boolean> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/invited-users/check`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email: email.toLowerCase() }),
    });

    if (!response.ok) {
      console.error('Failed to check invited user status:', response.statusText);
      return false;
    }

    const result = await response.json();
    return result.isInvited || false;
  } catch (error) {
    console.error('Error checking invited user:', error);
    return false;
  }
};

/**
 * Mark an invited user as used after successful signup/login
 */
export const markInvitedUserAsUsed = async (email: string): Promise<boolean> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/invited-users/mark-used`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email: email.toLowerCase() }),
    });

    if (!response.ok) {
      console.error('Failed to mark invited user as used:', response.statusText);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error marking invited user as used:', error);
    return false;
  }
};

/**
 * Get invited user details
 */
export const getInvitedUserDetails = async (email: string): Promise<InvitedUser | null> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/invited-users/details`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email: email.toLowerCase() }),
    });

    if (!response.ok) {
      return null;
    }

    const result = await response.json();
    return result.invitedUser || null;
  } catch (error) {
    console.error('Error getting invited user details:', error);
    return null;
  }
};

/**
 * Clean up invited user documents after they complete the flow
 */
export const cleanupInvitedUser = async (email: string): Promise<boolean> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/invited-users/cleanup`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email: email.toLowerCase() }),
    });

    if (!response.ok) {
      console.error('Failed to cleanup invited user:', response.statusText);
      return false;
    }

    const result = await response.json();
    return result.success;
  } catch (error) {
    console.error('Error cleaning up invited user:', error);
    return false;
  }
};